import { Bot, Database, Edit2, FileUp, Loader2, MessageSquare, Plus, Search, X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AgentCard } from "@/components/agents/AgentCard";
import { CreateAgentModal } from "@/components/agents/CreateAgentModal";
import { EditAgentModal } from "@/components/agents/EditAgentModal";
import { AgentProjectNotice, PageHeader, SortDropdown, useConfirm, useToast } from "@/components/shared";
import { ImportSkillModal } from "@/components/skills/ImportSkillModal";
import { featureFlags } from "@/config/featureFlags";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib";
import { agentService, dataSourceService, skillService, toolService } from "@/lib/services";
import type {
  Agent,
  AgentCreate,
  AgentSkill,
  AgentSkillAttach,
  AgentUpdate,
  DataCollection,
  SkillSummary,
  ToolSummary,
} from "@/types";

// How long the "New" badge stays on a just-created agent's card before fading away.
const NEW_AGENT_HIGHLIGHT_MS = 30_000;

interface AgentsNavigationState {
  newAgentUuid?: string;
}

export function Agents() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enabledModels, tierDefaults } = useApp();
  const { profile } = useAuth();
  const { confirm } = useConfirm();
  const { showToast } = useToast();

  const navState = location.state as AgentsNavigationState | null;

  const [agents, setAgents] = useState<Agent[]>([]);
  const [dataSources, setDataSources] = useState<DataCollection[]>([]);
  const [availableSkills, setAvailableSkills] = useState<SkillSummary[]>([]);
  const [availableTools, setAvailableTools] = useState<ToolSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportSkillModal, setShowImportSkillModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "my">("all");
  // Arriving here right after creating an agent (e.g. from the AI Builder)
  // defaults the sort to "Newest" so it isn't lost in the shuffle.
  const [sortBy, setSortBy] = useState<"popular" | "alphabetical" | "newest">(
    navState?.newAgentUuid ? "newest" : "popular",
  );
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [newlyCreatedUuid, setNewlyCreatedUuid] = useState<string | null>(navState?.newAgentUuid ?? null);

  const user = profile?.user;

  // Temporarily highlight a just-created agent, then clear both the
  // highlight and the navigation state (so refreshing this page doesn't
  // keep re-triggering it).
  useEffect(() => {
    if (!navState?.newAgentUuid) return;
    navigate(location.pathname, { replace: true, state: null });
    const timer = setTimeout(() => setNewlyCreatedUuid(null), NEW_AGENT_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [agentsRes, dataSourcesRes, toolsRes, skillsRes] = await Promise.all([
        agentService.list({ pageSize: 100 }),
        dataSourceService.list({ pageSize: 100 }),
        toolService.list({ pageSize: 100 }),
        skillService.list({ pageSize: 100 }),
      ]);
      setAgents(agentsRes.data);
      setDataSources(dataSourcesRes.data);
      setAvailableTools(toolsRes.data);
      setAvailableSkills(skillsRes.data);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (agentUuid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deletingUuid) return;

    const agent = agents.find((a) => a.uuid === agentUuid);
    if (!agent) return;

    // Look up the skill imported alongside this agent (if any).
    let siblingSkill: { uuid: string; name: string } | null = null;
    let attachedElsewhere = false;
    try {
      const impact = await agentService.deleteImpact(agentUuid);
      siblingSkill = impact.data.siblingSkill;
      attachedElsewhere = impact.data.siblingSkillAttachedElsewhere;
    } catch {
      // Non-fatal — fall back to a plain delete confirmation.
    }

    const confirmed = await confirm({
      title: "Delete Agent",
      message: "Are you sure you want to delete this agent? This action cannot be undone.",
      confirmText: "Delete",
      variant: "danger",
    });

    if (!confirmed) return;

    try {
      setDeletingUuid(agentUuid);
      await agentService.delete(agent.uuid);
      setAgents((prev) => prev.filter((a) => a.uuid !== agentUuid));
    } catch (err) {
      console.error("Failed to delete agent:", err);
      setDeletingUuid(null);
      return;
    } finally {
      setDeletingUuid(null);
    }

    // Offer to delete the skill imported alongside this agent.
    if (siblingSkill) {
      const warn = attachedElsewhere
        ? ` Note: "${siblingSkill.name}" is also attached to other agents, which will lose it.`
        : "";
      const alsoDelete = await confirm({
        title: "Delete imported skill?",
        message: `The skill "${siblingSkill.name}" was imported with this agent. Delete it too?${warn}`,
        confirmText: "Delete skill",
        variant: "danger",
      });
      if (alsoDelete) {
        try {
          await skillService.delete(siblingSkill.uuid);
          showToast("Imported skill deleted", "success");
        } catch (err) {
          showToast(getApiErrorMessage(err, "Failed to delete skill"), "error");
        }
      }
    }
  };

  const handleCreate = async (data: AgentCreate, skills: AgentSkillAttach[]) => {
    try {
      const response = await agentService.create(data);

      // Attach skills if any were selected
      if (skills.length > 0) {
        await agentService.attachSkills(response.data.uuid, skills);
      }

      setAgents((prev) => [response.data, ...prev]);
      setShowCreateModal(false);
    } catch (err) {
      console.error("Failed to create agent:", err);
    }
  };

  const handleStartChat = (agent: Agent, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/chat?agent=${agent.uuid}`);
  };

  const handleEdit = async (agent: Agent, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Fetch full agent details (including systemPrompt) before editing
      const response = await agentService.get(agent.uuid);
      setEditingAgent(response.data);
    } catch (err) {
      console.error("Failed to load agent details:", err);
      // Fall back to using the summary data
      setEditingAgent(agent);
    }
  };

  const handleUpdate = async (data: AgentUpdate) => {
    if (!editingAgent) return;

    try {
      const response = await agentService.update(editingAgent.uuid, data);
      setAgents((prev) => prev.map((a) => (a.uuid === editingAgent.uuid ? response.data : a)));
      setEditingAgent(null);
    } catch (err) {
      console.error("Failed to update agent:", err);
    }
  };

  const handleDuplicate = async (agent: Agent, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const duplicateData: AgentCreate = {
        name: `${agent.name} (Copy)`,
        description: agent.description,
        icon: agent.icon || "bot",
        color: agent.color || "#EC622B",
        systemPrompt: agent.systemPrompt || "You are a helpful AI assistant.",
        modelId: agent.modelId,
        teamUuid: agent.teamUuid,
        dataCollectionIds: agent.dataCollectionIds || [],
        // "Core" platform-type tools are excluded — they're implicit/always-on
        // now, so the duplicate doesn't need its own explicit AgentTool rows.
        toolConfigs: (agent.tools || [])
          .filter((t) => t.extension?.extensionType !== "platform")
          .map((t) => ({
            toolUuid: t.uuid,
            isEnabled: true,
          })),
      };

      // Fetch the source agent's attached skills so the duplicate keeps them too.
      const sourceSkills = await agentService.listSkills(agent.uuid).catch((err) => {
        console.error("Failed to load skills to duplicate:", err);
        return { data: [] as AgentSkill[] };
      });

      const response = await agentService.create(duplicateData);

      // Same core-skill exclusion as tools above.
      const addOnSourceSkills = sourceSkills.data.filter((as) => as.skill.extension?.extensionType !== "platform");
      if (addOnSourceSkills.length > 0) {
        const skillsToAttach: AgentSkillAttach[] = addOnSourceSkills.map((as) => ({
          skillUuid: as.skill.uuid,
          config: as.config,
          isEnabled: as.isEnabled,
          priority: as.priority,
        }));
        await agentService.attachSkills(response.data.uuid, skillsToAttach);
      }

      setAgents((prev) => [response.data, ...prev]);
    } catch (err) {
      console.error("Failed to duplicate agent:", err);
    }
  };

  const handleCardClick = async (agent: Agent) => {
    try {
      // Fetch full agent details for the detail modal
      const response = await agentService.get(agent.uuid);
      setSelectedAgent(response.data);
    } catch (err) {
      console.error("Failed to load agent details:", err);
      // Fall back to using the summary data
      setSelectedAgent(agent);
    }
  };

  const filteredAgents = agents
    .filter((agent) => {
      const matchesSearch =
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (agent.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
      const matchesFilter = filter === "all" || (filter === "my" && agent.createdByUuid === user?.uuid);
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "popular":
          return (b.usageCount ?? 0) - (a.usageCount ?? 0);
        case "alphabetical":
          return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        case "newest":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default:
          return 0;
      }
    });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  // ============================================================================
  // Main Agents Component Return
  // ============================================================================

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader
        icon={Bot}
        title="My Agents"
        subtitle="Create and manage custom AI agents with specific capabilities"
        actions={
          <div className="flex items-center gap-2">
            {featureFlags.skillImport && (
              <button
                onClick={() => setShowImportSkillModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-surface-primary hover:bg-interactive-hover border border-border-secondary text-content-primary font-medium rounded-lg transition"
              >
                <FileUp className="w-4 h-4" />
                Import Agent
              </button>
            )}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient hover:opacity-90 text-white font-medium rounded-lg transition"
            >
              <Plus className="w-4 h-4" />
              Create Agent
            </button>
          </div>
        }
      >
        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-quaternary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search agents..."
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <div className="flex gap-2">
            {(["all", "my"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  filter === f
                    ? "bg-brand-primary-subtle text-brand-primary border border-brand-primary/30"
                    : "bg-surface-primary text-content-tertiary border border-border-primary hover:bg-interactive-hover"
                }`}
              >
                {f === "all" ? "All Agents" : "My Agents"}
              </button>
            ))}
          </div>
          {/* Sort dropdown */}
          <div className="ml-auto">
            <SortDropdown
              options={[
                { id: "popular", label: "Popular" },
                { id: "alphabetical", label: "Alphabetical" },
                { id: "newest", label: "Newest" },
              ]}
              value={sortBy}
              onChange={setSortBy}
            />
          </div>
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {filteredAgents.length === 0 ? (
          <div className="bg-surface-primary border border-border-primary rounded-xl p-12 text-center">
            <Bot className="w-16 h-16 mx-auto text-content-quaternary mb-4" />
            <h3 className="text-xl font-semibold text-content-primary mb-2">
              {searchQuery || filter !== "all" ? "No agents found" : "No agents yet"}
            </h3>
            <p className="text-content-tertiary mb-6 max-w-md mx-auto">
              {searchQuery || filter !== "all"
                ? "Try adjusting your search or filter criteria"
                : "Create your first agent to customize AI behavior with system prompts, tools, and data sources."}
            </p>
            {!searchQuery && filter === "all" && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gradient hover:opacity-90 text-white font-semibold rounded-lg transition"
              >
                <Plus className="w-5 h-5" />
                Create Agent
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <AgentCard
                key={agent.uuid}
                agent={agent}
                newlyCreatedUuid={newlyCreatedUuid}
                deletingUuid={deletingUuid}
                onCardClick={handleCardClick}
                onStartChat={handleStartChat}
                onDuplicate={handleDuplicate}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {showImportSkillModal && (
        <ImportSkillModal
          title="Import Agent"
          onClose={() => setShowImportSkillModal(false)}
          onImported={() => loadData()}
        />
      )}

      {showCreateModal && (
        <CreateAgentModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
          models={enabledModels}
          tierDefaults={tierDefaults}
          dataSources={dataSources}
          availableSkills={availableSkills}
          availableTools={availableTools}
        />
      )}

      {editingAgent && (
        <EditAgentModal
          agent={editingAgent}
          onClose={() => setEditingAgent(null)}
          onSave={handleUpdate}
          models={enabledModels}
          tierDefaults={tierDefaults}
          dataSources={dataSources}
          availableSkills={availableSkills}
          availableTools={availableTools}
        />
      )}

      {selectedAgent && (
        <AgentDetailModal
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
          onEdit={(agent) => {
            setSelectedAgent(null);
            setEditingAgent(agent);
          }}
          onStartChat={(agent) => {
            setSelectedAgent(null);
            navigate(`/chat?agent=${agent.uuid}`);
          }}
          availableSkills={availableSkills}
        />
      )}
    </div>
  );
}
// ============================================================================
// Agent Detail Modal
// ============================================================================

interface AgentDetailModalProps {
  agent: Agent;
  onClose: () => void;
  onEdit: (agent: Agent) => void;
  onStartChat: (agent: Agent) => void;
  availableSkills: SkillSummary[];
}

function AgentDetailModal({
  agent,
  onClose,
  onEdit,
  onStartChat,
  availableSkills: _availableSkills,
}: Readonly<AgentDetailModalProps>) {
  const [agentSkills, setAgentSkills] = useState<AgentSkill[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);

  useEffect(() => {
    const loadSkills = async () => {
      try {
        setIsLoadingSkills(true);
        const response = await agentService.listSkills(agent.uuid);
        // Exclude "core" platform-type skills — they're implicit/always-on now,
        // so listing them alongside deliberately-attached add-ons is just noise.
        setAgentSkills(response.data.filter((as) => as.skill.extension?.extensionType !== "platform"));
      } catch (err) {
        console.error("Failed to load agent skills:", err);
      } finally {
        setIsLoadingSkills(false);
      }
    };
    loadSkills();
  }, [agent.uuid]);

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center p-4 z-50">
      <div className="bg-surface-primary border border-border-primary rounded-2xl max-w-2xl w-full h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: `${agent.color || "#EC622B"}20`,
              }}
            >
              <Bot className="w-6 h-6" style={{ color: agent.color || "#EC622B" }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-content-primary">{agent.name}</h2>
              {agent.currentUserRole && (
                <p className="text-sm text-content-tertiary capitalize">Your role: {agent.currentUserRole}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <AgentProjectNotice agent={agent} className="mx-6" />

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <h3 className="text-sm font-medium text-content-tertiary mb-2">Description</h3>
            <p className="text-content-primary">{agent.description || "No description"}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-content-tertiary mb-2">System Prompt</h3>
            <p className="text-content-primary whitespace-pre-wrap bg-surface-secondary p-4 rounded-lg">
              {agent.systemPrompt || "No system prompt"}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-content-tertiary mb-2">Model</h3>
            <p className="text-content-primary">{agent.model?.displayName || agent.model?.name || agent.modelId}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-content-tertiary mb-2">Statistics</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface-secondary p-4 rounded-lg">
                <div className="text-content-tertiary text-sm">Total Uses</div>
                <div className="text-2xl font-bold text-content-primary">{agent.usageCount}</div>
              </div>
              <div className="bg-surface-secondary p-4 rounded-lg">
                <div className="text-content-tertiary text-sm">Data Sources</div>
                <div className="text-2xl font-bold text-content-primary">{agent.dataCollectionIds?.length || 0}</div>
              </div>
            </div>
          </div>

          {agent.dataCollectionIds && agent.dataCollectionIds.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-content-tertiary mb-2">Connected Data Sources</h3>
              <div className="flex items-center gap-2 text-content-secondary">
                <Database className="w-4 h-4" />
                <span>{agent.dataCollectionIds.length} data source(s) connected</span>
              </div>
            </div>
          )}

          {/* Skills Section */}
          <div>
            <h3 className="text-sm font-medium text-content-tertiary mb-2">Attached Skills</h3>
            {(() => {
              if (isLoadingSkills) {
                return (
                  <div className="flex items-center gap-2 text-content-tertiary">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading skills...</span>
                  </div>
                );
              }
              if (agentSkills.length > 0) {
                return (
                  <div className="space-y-2">
                    {agentSkills
                      .sort((a, b) => b.priority - a.priority)
                      .map((as) => (
                        <div
                          key={as.skill.uuid}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${
                            as.isEnabled
                              ? "bg-amber-500/10 border-amber-500/30"
                              : "bg-surface-secondary/50 border-border-secondary opacity-60"
                          }`}
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${as.skill.color || "#F59E0B"}20` }}
                          >
                            <Zap className="w-4 h-4" style={{ color: as.skill.color || "#F59E0B" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-content-primary text-sm font-medium">{as.skill.name}</div>
                            <div className="text-content-tertiary text-xs">
                              {as.skill.category || as.skill.skillType} • Priority {as.priority + 1}
                              {!as.isEnabled && " • Disabled"}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                );
              }
              return (
                <div className="flex items-center gap-2 text-content-quaternary">
                  <Zap className="w-4 h-4" />
                  <span>No skills attached</span>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="border-t border-border-primary p-6 flex gap-3">
          {agent.canEdit && (
            <button
              onClick={() => onEdit(agent)}
              className="flex-1 px-6 py-3 bg-surface-secondary hover:bg-interactive-active text-content-primary font-semibold rounded-lg transition border border-border-secondary flex items-center justify-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit Agent
            </button>
          )}
          <button
            onClick={() => onStartChat(agent)}
            className="flex-1 px-6 py-3 bg-brand-gradient hover:opacity-90 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Start Chat
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Create Agent Modal (extracted to @/components/agents/CreateAgentModal.tsx)
// ============================================================================
